'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

type FormStatus = 'idle' | 'loading' | 'success' | 'error';
type UploadStatus = 'idle' | 'uploading' | 'scanning' | 'complete' | 'error';

// Allowed file types for upload
const ALLOWED_FILE_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

const MAX_FILE_SIZE = 2 * 1024 * 1024 * 1024; // 2GB

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

const COMPANY_STAGES = [
  { value: '', label: 'Select stage...' },
  { value: 'pre-seed', label: 'Pre-seed' },
  { value: 'seed', label: 'Seed' },
  { value: 'series-a', label: 'Series A' },
  { value: 'series-b', label: 'Series B' },
  { value: 'growth', label: 'Growth / Series C+' },
  { value: 'established', label: 'Established / Profitable' },
];

const TEAM_SIZES = [
  { value: '', label: 'Select size...' },
  { value: '1-5', label: '1-5 engineers' },
  { value: '6-15', label: '6-15 engineers' },
  { value: '16-30', label: '16-30 engineers' },
  { value: '31-50', label: '31-50 engineers' },
  { value: '51-100', label: '51-100 engineers' },
  { value: '100+', label: '100+ engineers' },
];

const INDUSTRIES = [
  { value: '', label: 'Select industry...' },
  { value: 'saas', label: 'B2B SaaS' },
  { value: 'fintech', label: 'Fintech / Payments' },
  { value: 'marketplace', label: 'Marketplace / E-commerce' },
  { value: 'healthtech', label: 'Healthtech / Biotech' },
  { value: 'edtech', label: 'Edtech' },
  { value: 'ai-ml', label: 'AI / ML' },
  { value: 'devtools', label: 'Developer Tools / Infrastructure' },
  { value: 'consumer', label: 'Consumer / B2C' },
  { value: 'enterprise', label: 'Enterprise Software' },
  { value: 'other', label: 'Other' },
];

const PRIMARY_CHALLENGES = [
  { value: '', label: 'Select challenge...' },
  { value: 'no-cto', label: 'No CTO / Need technical leadership' },
  { value: 'scaling', label: 'Scaling the engineering team' },
  { value: 'architecture', label: 'Architecture decisions / Technical debt' },
  { value: 'hiring', label: 'Hiring and building the team' },
  { value: 'process', label: 'Engineering processes and delivery' },
  { value: 'acquisition', label: 'M&A / Due diligence preparation' },
  { value: 'other', label: 'Other' },
];

const INTERESTED_TIERS = [
  { value: '', label: 'Select tier...' },
  { value: 'strategic', label: 'Strategic Advisor ($2,500 - $4,000/month)' },
  { value: 'engaged', label: 'Engaged Partner ($6,000 - $10,000/month)' },
  { value: 'embedded', label: 'Embedded CTO ($15,000 - $25,000/month)' },
  { value: 'unsure', label: 'Not sure yet - help me decide' },
];

interface CTOOfficeInquiryFormProps {
  preselectedTier?: string;
}

// Extend window for Turnstile
declare global {
  interface Window {
    turnstile?: {
      render: (container: string | HTMLElement, options: {
        sitekey: string;
        callback: (token: string) => void;
        'error-callback'?: () => void;
        'expired-callback'?: () => void;
        theme?: 'light' | 'dark' | 'auto';
        size?: 'normal' | 'compact';
      }) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
    };
  }
}

export default function CTOOfficeInquiryForm({ preselectedTier }: CTOOfficeInquiryFormProps) {
  const [companyName, setCompanyName] = useState('');
  const [companyWebsite, setCompanyWebsite] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [role, setRole] = useState('');
  const [companyStage, setCompanyStage] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [industry, setIndustry] = useState('');
  const [primaryChallenge, setPrimaryChallenge] = useState('');
  const [challengeDescription, setChallengeDescription] = useState('');
  const [interestedTier, setInterestedTier] = useState(preselectedTier || '');
  const [status, setStatus] = useState<FormStatus>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  // File upload state
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadError, setUploadError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Turnstile state
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState<string | null>(null);
  const turnstileRef = useRef<HTMLDivElement>(null);
  const widgetIdRef = useRef<string | null>(null);

  // Fetch Turnstile site key from features API
  useEffect(() => {
    fetch('/api/public/features')
      .then(res => res.json())
      .then(data => {
        if (data.turnstileSiteKey) {
          setTurnstileSiteKey(data.turnstileSiteKey);
        }
      })
      .catch(() => {
        // Turnstile not configured, form will work without it
      });
  }, []);

  // Turnstile callback
  const onTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
  }, []);

  // Load and render Turnstile widget
  useEffect(() => {
    if (!turnstileSiteKey || !turnstileRef.current) return;

    // Load Turnstile script if not already loaded
    const scriptId = 'turnstile-script';
    if (!document.getElementById(scriptId)) {
      const script = document.createElement('script');
      script.id = scriptId;
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      document.head.appendChild(script);
    }

    // Render widget when script is loaded
    const renderWidget = () => {
      if (window.turnstile && turnstileRef.current && !widgetIdRef.current) {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: turnstileSiteKey,
          callback: onTurnstileVerify,
          'error-callback': () => setTurnstileToken(null),
          'expired-callback': () => setTurnstileToken(null),
          theme: 'dark',
        });
      }
    };

    // Check if already loaded or wait for load
    if (window.turnstile) {
      renderWidget();
    } else {
      const script = document.getElementById(scriptId);
      script?.addEventListener('load', renderWidget);
    }

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [turnstileSiteKey, onTurnstileVerify]);

  // File selection handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setUploadError('');

    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setUploadError('Please upload a PDF, Word, PowerPoint, Excel, TXT, or CSV file.');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setUploadError('File size must be under 2GB.');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    setSelectedFile(file);
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadError('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation
    if (!companyName.trim() || !contactName.trim() || !contactEmail.trim() || !challengeDescription.trim()) {
      setErrorMessage('Please fill in all required fields.');
      setStatus('error');
      return;
    }

    if (challengeDescription.trim().length < 50) {
      setErrorMessage('Please provide more detail about your challenge (at least 50 characters).');
      setStatus('error');
      return;
    }

    // Require Turnstile verification if configured
    if (turnstileSiteKey && !turnstileToken) {
      setErrorMessage('Please complete the security verification.');
      setStatus('error');
      return;
    }

    setStatus('loading');
    setErrorMessage('');
    setUploadStatus('idle');
    setUploadProgress(0);

    try {
      // 1. Submit form data with optional file metadata
      // If file is selected, include metadata to get presigned URL in single call
      const response = await fetch('/api/public/cto-office-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: companyName.trim(),
          companyWebsite: companyWebsite.trim() || undefined,
          contactName: contactName.trim(),
          contactEmail: contactEmail.trim(),
          role: role.trim(),
          companyStage,
          teamSize,
          industry,
          primaryChallenge,
          challengeDescription: challengeDescription.trim(),
          interestedTier: interestedTier || 'unsure',
          _honeypot: '',
          _turnstileToken: turnstileToken || '',
          // Include file metadata if file selected (enables single-call presigned URL)
          ...(selectedFile
            ? {
                fileName: selectedFile.name,
                fileSize: selectedFile.size,
                fileType: selectedFile.type,
              }
            : {}),
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Form submission failed');
      }

      const inquiryId = data.inquiryId;

      // 2. If file selected and we have an inquiryId, upload it
      if (selectedFile && inquiryId) {
        setUploadStatus('uploading');

        // Check if presigned URL was returned in form response (optimized single-call flow)
        let uploadUrl = data.uploadUrl;
        let fileKey = data.fileKey;

        // Fall back to separate upload auth call if presigned URL not in response
        if (!uploadUrl || !fileKey) {
          const uploadAuthResponse = await fetch('/api/public/cto-office-upload', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              inquiryId,
              fileName: selectedFile.name,
              fileSize: selectedFile.size,
              fileType: selectedFile.type,
            }),
          });

          const uploadAuth = await uploadAuthResponse.json();

          if (!uploadAuthResponse.ok || !uploadAuth.success) {
            console.error('File upload auth failed:', uploadAuth.error);
            setUploadStatus('error');
            setUploadError(uploadAuth.error || 'Failed to prepare file upload');
            // Continue to success state - form was submitted
            setStatus('success');
            return;
          }

          uploadUrl = uploadAuth.uploadUrl;
          fileKey = uploadAuth.fileKey;
        }

        // Upload file directly to R2 using presigned URL
        try {
          // Calculate timeout based on file size (min 60s, ~1MB/s expected)
          const uploadTimeoutMs = Math.max(60000, Math.ceil(selectedFile.size / (1024 * 1024)) * 5000);

          await new Promise<void>((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            let timeoutId: ReturnType<typeof setTimeout>;

            xhr.upload.onprogress = (e) => {
              if (e.lengthComputable) {
                setUploadProgress(Math.round((e.loaded / e.total) * 100));
                // Reset timeout on progress (upload is still active)
                if (timeoutId) clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                  xhr.abort();
                  reject(new Error('Upload stalled - please check your connection and try again'));
                }, 30000); // 30s without progress = stalled
              }
            };

            xhr.onload = () => {
              if (timeoutId) clearTimeout(timeoutId);
              if (xhr.status === 200) {
                resolve();
              } else if (xhr.status === 403) {
                reject(new Error('Upload authorization expired. Please try submitting the form again.'));
              } else {
                reject(new Error(`Upload failed (status ${xhr.status}). Please try again.`));
              }
            };

            xhr.onerror = () => {
              if (timeoutId) clearTimeout(timeoutId);
              reject(new Error('Upload failed - please check your internet connection'));
            };
            xhr.onabort = () => {
              if (timeoutId) clearTimeout(timeoutId);
              reject(new Error('Upload was cancelled'));
            };
            xhr.ontimeout = () => {
              if (timeoutId) clearTimeout(timeoutId);
              reject(new Error('Upload timed out - please try again'));
            };

            // Set overall timeout
            xhr.timeout = uploadTimeoutMs;

            xhr.open('PUT', uploadUrl);
            xhr.setRequestHeader('Content-Type', selectedFile.type);
            xhr.send(selectedFile);

            // Start stall detection
            timeoutId = setTimeout(() => {
              xhr.abort();
              reject(new Error('Upload stalled - please check your connection'));
            }, 30000);
          });

          // Finalize upload (triggers virus scan)
          setUploadStatus('scanning');

          const finalizeResponse = await fetch('/api/public/cto-office-finalize', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              inquiryId,
              fileKey,
            }),
          });

          const finalizeData = await finalizeResponse.json();

          if (finalizeResponse.ok && finalizeData.success) {
            setUploadStatus('complete');
          } else {
            setUploadStatus('error');
            setUploadError(finalizeData.error || 'Failed to process uploaded file');
          }
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          setUploadStatus('error');
          setUploadError('File upload failed. Your inquiry was still submitted.');
        }
      }

      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to connect. Please check your internet connection and try again.'
      );
      setUploadStatus('idle');
      // Reset Turnstile for retry
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current);
        setTurnstileToken(null);
      }
    }
  };

  if (status === 'success') {
    return (
      <div
        role="status"
        aria-live="polite"
        className="bg-accent-muted border border-border rounded-lg p-8 text-center"
      >
        <div className="text-5xl mb-4">&#10003;</div>
        <p className="text-accent font-bold text-xl mb-3">Thank you for reaching out</p>
        <p className="text-light-text-secondary dark:text-foreground-secondary mb-4">
          We have received your inquiry and will review it carefully. Expect a response within 1-2 business days with a custom proposal based on your needs.
        </p>
        <p className="text-sm text-light-text-tertiary dark:text-foreground-tertiary">
          Check your email ({contactEmail}) for a confirmation.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Company Name */}
        <div>
          <label
            htmlFor="company-name"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Company Name <span className="text-accent">*</span>
          </label>
          <input
            type="text"
            id="company-name"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="Acme Inc."
            required
            maxLength={100}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          />
        </div>

        {/* Company Website */}
        <div>
          <label
            htmlFor="company-website"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Company Website
          </label>
          <input
            type="url"
            id="company-website"
            value={companyWebsite}
            onChange={(e) => setCompanyWebsite(e.target.value)}
            placeholder="https://acme.com"
            maxLength={255}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          />
        </div>

        {/* Contact Name */}
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Your Name <span className="text-accent">*</span>
          </label>
          <input
            type="text"
            id="contact-name"
            value={contactName}
            onChange={(e) => setContactName(e.target.value)}
            placeholder="Jane Smith"
            required
            maxLength={100}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          />
        </div>

        {/* Email */}
        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Email <span className="text-accent">*</span>
          </label>
          <input
            type="email"
            id="contact-email"
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder="jane@acme.com"
            required
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          />
        </div>

        {/* Role */}
        <div>
          <label
            htmlFor="role"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Your Role
          </label>
          <input
            type="text"
            id="role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="CEO, Founder, VP Engineering..."
            maxLength={100}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          />
        </div>

        {/* Company Stage */}
        <div>
          <label
            htmlFor="company-stage"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Company Stage
          </label>
          <select
            id="company-stage"
            value={companyStage}
            onChange={(e) => setCompanyStage(e.target.value)}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          >
            {COMPANY_STAGES.map((stage) => (
              <option key={stage.value} value={stage.value}>
                {stage.label}
              </option>
            ))}
          </select>
        </div>

        {/* Team Size */}
        <div>
          <label
            htmlFor="team-size"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Engineering Team Size
          </label>
          <select
            id="team-size"
            value={teamSize}
            onChange={(e) => setTeamSize(e.target.value)}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          >
            {TEAM_SIZES.map((size) => (
              <option key={size.value} value={size.value}>
                {size.label}
              </option>
            ))}
          </select>
        </div>

        {/* Industry */}
        <div>
          <label
            htmlFor="industry"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Industry
          </label>
          <select
            id="industry"
            value={industry}
            onChange={(e) => setIndustry(e.target.value)}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          >
            {INDUSTRIES.map((ind) => (
              <option key={ind.value} value={ind.value}>
                {ind.label}
              </option>
            ))}
          </select>
        </div>

        {/* Primary Challenge */}
        <div>
          <label
            htmlFor="primary-challenge"
            className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
          >
            Primary Challenge
          </label>
          <select
            id="primary-challenge"
            value={primaryChallenge}
            onChange={(e) => setPrimaryChallenge(e.target.value)}
            className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
            disabled={status === 'loading'}
          >
            {PRIMARY_CHALLENGES.map((challenge) => (
              <option key={challenge.value} value={challenge.value}>
                {challenge.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Interested Tier */}
      <div>
        <label
          htmlFor="interested-tier"
          className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
        >
          Engagement Level of Interest
        </label>
        <select
          id="interested-tier"
          value={interestedTier}
          onChange={(e) => setInterestedTier(e.target.value)}
          className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all"
          disabled={status === 'loading'}
        >
          {INTERESTED_TIERS.map((tier) => (
            <option key={tier.value} value={tier.value}>
              {tier.label}
            </option>
          ))}
        </select>
      </div>

      {/* Challenge Description */}
      <div>
        <label
          htmlFor="challenge-description"
          className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
        >
          Tell us about your situation <span className="text-accent">*</span>
        </label>
        <textarea
          id="challenge-description"
          value={challengeDescription}
          onChange={(e) => setChallengeDescription(e.target.value)}
          placeholder="What are your biggest technical challenges? What would success look like? Any context that helps us understand your needs..."
          required
          rows={5}
          minLength={50}
          maxLength={2000}
          className="w-full px-4 py-3 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground placeholder:text-light-text-secondary/50 dark:placeholder:text-foreground-muted focus:outline-none focus:ring-2 focus:ring-ring/50 transition-all resize-y"
          disabled={status === 'loading'}
        />
        <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
          {challengeDescription.length}/2000 (minimum 50 characters)
        </p>
      </div>

      {/* File Upload */}
      <div>
        <label
          htmlFor="file-upload"
          className="block text-sm font-medium text-light-text dark:text-foreground-secondary mb-2"
        >
          Attach Document (Optional)
        </label>
        <div className="flex items-center gap-4">
          <input
            type="file"
            id="file-upload"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.csv"
            className="hidden"
            disabled={status === 'loading'}
          />
          <label
            htmlFor="file-upload"
            className={`cursor-pointer px-4 py-2 bg-light-surface dark:bg-background border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-border-muted0 transition-colors ${
              status === 'loading' ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            {selectedFile ? 'Change File' : 'Choose File'}
          </label>
          {selectedFile && (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-light-text-secondary dark:text-foreground-tertiary text-sm truncate">
                {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-red-500 hover:text-red-400 text-sm flex-shrink-0"
                disabled={status === 'loading'}
              >
                Remove
              </button>
            </div>
          )}
        </div>
        <p className="text-xs text-light-text-tertiary dark:text-foreground-tertiary mt-1">
          PDF, Word, PowerPoint, Excel, TXT, or CSV up to 2GB. Files are scanned for viruses before review.
        </p>

        {/* Upload Progress */}
        {uploadStatus === 'uploading' && (
          <div className="mt-3">
            <div className="h-2 bg-light-surface dark:bg-surface rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
              Uploading... {uploadProgress}%
            </p>
          </div>
        )}

        {uploadStatus === 'scanning' && (
          <p className="text-xs text-accent mt-2">
            File uploaded. Scanning for viruses...
          </p>
        )}

        {uploadStatus === 'complete' && (
          <p className="text-xs text-green-500 mt-2">
            File uploaded and queued for scanning.
          </p>
        )}

        {uploadError && (
          <div className="mt-2">
            <p className="text-xs text-red-500">{uploadError}</p>
            {uploadStatus === 'error' && (
              <p className="text-xs text-light-text-secondary dark:text-foreground-tertiary mt-1">
                Your inquiry will still be submitted. You can attach a file by creating a new inquiry if needed.
              </p>
            )}
          </div>
        )}
      </div>

      {/* Honeypot (hidden) */}
      <div className="hidden" aria-hidden="true">
        <input type="text" name="_honeypot" tabIndex={-1} autoComplete="off" />
      </div>

      {/* Turnstile Widget */}
      {turnstileSiteKey && (
        <div className="flex justify-center">
          <div ref={turnstileRef} />
        </div>
      )}

      {/* Error Message */}
      {status === 'error' && errorMessage && (
        <div
          role="alert"
          aria-live="polite"
          className="bg-red-500/10 border border-red-500/30 rounded-lg p-3"
        >
          <p className="text-red-600 dark:text-red-400 text-sm">{errorMessage}</p>
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full bg-accent hover:bg-accent-hover disabled:bg-accent-muted0 disabled:cursor-not-allowed text-accent-foreground font-semibold py-4 px-6 rounded-lg transition-colors text-lg"
      >
        {status === 'loading' ? 'Submitting...' : 'Start the Conversation'}
      </button>

      <p className="text-xs text-center text-light-text-tertiary dark:text-foreground-tertiary">
        We typically respond within 1-2 business days with a custom proposal.
      </p>
    </form>
  );
}
